import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { send } from "../api/auth";
import { console_ } from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { useSession } from "../session/SessionProvider";
import { whenIn } from "../system/format";

// ---------------------------------------------------------------------------
// SWEEP S22. WHAT RUNS OUT, ACROSS THE WHOLE BENCH — the list a compliance officer works
// from. Expired first, then by date; each with the person's next booked shift, because a
// card that lapses before that shift stops the booking (V8's gate) and one that lapses
// after it does not yet.
// ---------------------------------------------------------------------------

type Expiring = {
  worker_id: string;
  worker_name: string;
  credential_name: string;
  is_blocking: boolean;
  expires_on: string;
  days_left: number;
  next_shift_starts_at: string | null;
};

const WINDOWS = [30, 60, 90];

export function ExpiringSoon() {
  const { apiBase, me } = useSession();
  const { t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const [within, setWithin] = useState(30);
  const [rows, setRows] = useState<Expiring[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    send<{ expiring?: Expiring[]; error?: { message?: string } }>(console_.expiringCredentials(apiBase, within))
      .then((res) => {
        if (res.status === 200) {
          setRows(res.body.expiring ?? []);
          setFailed(null);
        } else setFailed(refusal(res.body.error) ?? t("The expiry list could not be read."));
      })
      .catch(() => setFailed(t("We could not reach Verbunk. Check your connection and try again.")));
  }, [apiBase, within]);

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Expiring soon")}</h2>
        <label className="visually-hidden" htmlFor="expiring-within">
          {t("Within")}
        </label>
        <select id="expiring-within" value={within} onChange={(e) => setWithin(Number(e.target.value))}>
          {WINDOWS.map((d) => (
            <option key={d} value={d}>
              {t("Within {n} days", { n: d })}
            </option>
          ))}
        </select>
      </div>
      {failed ? <StatusChip tone="failed" label={failed} /> : null}
      {rows !== null && rows.length === 0 ? (
        <p className="muted">
          {t("Nothing on the bench expires within {n} days, and nothing has already expired.", { n: within })}
        </p>
      ) : null}
      {rows !== null && rows.length > 0 ? (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">{t("Person")}</th>
              <th scope="col">{t("Credential")}</th>
              <th scope="col">{t("Expires")}</th>
              <th scope="col">{t("Next shift")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const lapsesFirst =
                r.next_shift_starts_at !== null && new Date(`${r.expires_on}T23:59:59`) < new Date(r.next_shift_starts_at);
              return (
                <tr key={`${r.worker_id}-${r.credential_name}-${r.expires_on}`}>
                  <th scope="row">
                    <Link to={`/console/workers/${r.worker_id}`}>{r.worker_name}</Link>
                  </th>
                  <td>
                    {r.credential_name}
                    {r.is_blocking ? null : <div className="muted">{t("Advisory")}</div>}
                  </td>
                  <td>
                    {r.days_left < 0 ? (
                      <StatusChip tone="failed" label={t("Expired {n} day(s) ago", { n: -r.days_left })} />
                    ) : (
                      <StatusChip
                        tone="attention"
                        label={r.days_left === 0 ? t("Expires today") : t("In {n} day(s)", { n: r.days_left })}
                      />
                    )}
                    <div className="muted">{r.expires_on}</div>
                  </td>
                  <td>
                    {r.next_shift_starts_at ? whenIn(r.next_shift_starts_at, orgZone) : <span className="muted">{t("None booked")}</span>}
                    {lapsesFirst && r.is_blocking ? <div className="muted">{t("Lapses before this shift")}</div> : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}
